/**
 * Authored water sheet, grid stroke, deck flock, waterline hint, under-cap rim,
 * and aim chrome (`MapDocument.look`, schema v13 extras plus later look fills).
 *
 * Independent of `timeOfDay`, which still grades the sky, beds, and camera — not this sheet.
 * `lookFromEnvironment` exists only so a pre-v5 file migrates to the sheet that Time used to
 * paint. Randomize and the Time dropdown must not call it again after load.
 */

import { clampRgba, isRgba, rgbaFromRgb24, type Rgba } from '../color.ts';
import { clamp } from '../math.ts';
import { DAY_WATER_ALPHA, DAY_WATER_COLOR, waterAppearance } from '../environment/water.ts';
import {
  cloneAimChrome,
  DEFAULT_AIM_CHROME,
  migrateAimChrome,
  type AimChrome,
} from './aimChrome.ts';
import { DEFAULT_CAP_FLOCK_AMOUNT, DEFAULT_CAP_FLOCK_ENABLED } from './capFlock.ts';
import type { Environment } from './environment.ts';
import { DEFAULT_WATERLINE_HINT_ALPHA } from './extrude.ts';
import { evaluateSun } from './sun.ts';

export type { AimChrome } from './aimChrome.ts';

/** Previous hard-coded over-cap rim stroke (`AREA_STROKE_ALPHA`). Missing look fields stay this. */
export const DEFAULT_UNDER_CAP_RIM_ALPHA = 0.75;

export type MapLook = {
  waterColor: Rgba;
  gridColor: Rgba;
  capFlockEnabled: boolean;
  capFlockAmount: number;
  waterlineHintAlpha: number;
  underCapRimAlpha: number;
  aimChrome: AimChrome;
};

/** Same stroke the pre-v5 renderer hard-coded in `view.ts`. */
export const DEFAULT_GRID_COLOR = 0x143044;
export const DEFAULT_GRID_ALPHA = 0.45;

export const DEFAULT_LOOK: MapLook = {
  waterColor: rgbaFromRgb24(DAY_WATER_COLOR, DAY_WATER_ALPHA),
  gridColor: rgbaFromRgb24(DEFAULT_GRID_COLOR, DEFAULT_GRID_ALPHA),
  capFlockEnabled: DEFAULT_CAP_FLOCK_ENABLED,
  capFlockAmount: DEFAULT_CAP_FLOCK_AMOUNT,
  waterlineHintAlpha: DEFAULT_WATERLINE_HINT_ALPHA,
  underCapRimAlpha: DEFAULT_UNDER_CAP_RIM_ALPHA,
  aimChrome: cloneAimChrome(DEFAULT_AIM_CHROME),
};

export type MapLookPatch = {
  waterColor?: Rgba;
  gridColor?: Rgba;
  capFlockEnabled?: boolean;
  capFlockAmount?: number;
  waterlineHintAlpha?: number;
  underCapRimAlpha?: number;
  aimChrome?: AimChrome;
};

function lookExtrasFromUnknown(value: Record<string, unknown> | null): Pick<
  MapLook,
  | 'capFlockEnabled'
  | 'capFlockAmount'
  | 'waterlineHintAlpha'
  | 'underCapRimAlpha'
  | 'aimChrome'
> {
  const record = value ?? {};

  return {
    capFlockEnabled:
      typeof record.capFlockEnabled === 'boolean'
        ? record.capFlockEnabled
        : DEFAULT_CAP_FLOCK_ENABLED,
    capFlockAmount: clamp(
      typeof record.capFlockAmount === 'number' && Number.isFinite(record.capFlockAmount)
        ? record.capFlockAmount
        : DEFAULT_CAP_FLOCK_AMOUNT,
      0,
      1,
    ),
    waterlineHintAlpha: clamp(
      typeof record.waterlineHintAlpha === 'number' && Number.isFinite(record.waterlineHintAlpha)
        ? record.waterlineHintAlpha
        : DEFAULT_WATERLINE_HINT_ALPHA,
      0,
      1,
    ),
    underCapRimAlpha: clamp(
      typeof record.underCapRimAlpha === 'number' && Number.isFinite(record.underCapRimAlpha)
        ? record.underCapRimAlpha
        : DEFAULT_UNDER_CAP_RIM_ALPHA,
      0,
      1,
    ),
    aimChrome: migrateAimChrome(record.aimChrome),
  };
}

export function createDefaultLook(): MapLook {
  return {
    waterColor: clampRgba(DEFAULT_LOOK.waterColor),
    gridColor: clampRgba(DEFAULT_LOOK.gridColor),
    ...lookExtrasFromUnknown(null),
  };
}

export function cloneLook(look: MapLook): MapLook {
  return {
    waterColor: clampRgba(look.waterColor),
    gridColor: clampRgba(look.gridColor),
    capFlockEnabled: look.capFlockEnabled,
    capFlockAmount: clamp(look.capFlockAmount, 0, 1),
    waterlineHintAlpha: clamp(look.waterlineHintAlpha, 0, 1),
    underCapRimAlpha: clamp(look.underCapRimAlpha, 0, 1),
    aimChrome: cloneAimChrome(look.aimChrome),
  };
}

/**
 * What a pre-v5 renderer would have painted for this environment: Time-driven sheet plus the
 * old grid constants. Used only when migrating a file that has no `look`.
 */
export function lookFromEnvironment(environment: Pick<Environment, 'timeOfDay' | 'sun'>): MapLook {
  const water = waterAppearance(evaluateSun(environment).skyLight);

  return {
    waterColor: rgbaFromRgb24(water.color, water.alpha),
    gridColor: rgbaFromRgb24(DEFAULT_GRID_COLOR, DEFAULT_GRID_ALPHA),
    ...lookExtrasFromUnknown(null),
  };
}

export function updateLook(look: MapLook, patch: MapLookPatch): void {
  if (patch.waterColor !== undefined) {
    look.waterColor = clampRgba(patch.waterColor);
  }

  if (patch.gridColor !== undefined) {
    look.gridColor = clampRgba(patch.gridColor);
  }

  if (patch.capFlockEnabled !== undefined) {
    look.capFlockEnabled = patch.capFlockEnabled;
  }

  if (patch.capFlockAmount !== undefined) {
    look.capFlockAmount = clamp(patch.capFlockAmount, 0, 1);
  }

  if (patch.waterlineHintAlpha !== undefined) {
    look.waterlineHintAlpha = clamp(patch.waterlineHintAlpha, 0, 1);
  }

  if (patch.underCapRimAlpha !== undefined) {
    look.underCapRimAlpha = clamp(patch.underCapRimAlpha, 0, 1);
  }

  if (patch.aimChrome !== undefined) {
    look.aimChrome = cloneAimChrome(patch.aimChrome);
  }
}

/** Fill v13 look extras onto a migrated sheet (water/grid already resolved). */
export function lookWithV13Extras(
  base: Pick<MapLook, 'waterColor' | 'gridColor'>,
  rawLook: unknown,
): MapLook {
  const record =
    typeof rawLook === 'object' && rawLook !== null && !Array.isArray(rawLook)
      ? (rawLook as Record<string, unknown>)
      : null;

  return {
    waterColor: clampRgba(base.waterColor),
    gridColor: clampRgba(base.gridColor),
    ...lookExtrasFromUnknown(record),
  };
}

/** v5 split RGB+alpha, v6 RGBA, or junk → a clamped `Rgba`. */
export function lookColorFromUnknown(value: unknown, alpha: unknown, fallback: Rgba): Rgba {
  if (isRgba(value)) {
    return clampRgba(value);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const a = typeof alpha === 'number' && Number.isFinite(alpha) ? alpha : fallback.a;

    return rgbaFromRgb24(value, a);
  }

  return clampRgba(fallback);
}
